// Hotel Mode helpers — completely separate from the family Google-auth flow.
// Storage: single string in localStorage under `poodletalk_hotel_device_token`.
// On every app boot we call /api/hotel/validate to confirm the device + hotel
// are still active. If not (revoked / paused / cancelled) we clear the token
// so the UI falls back to guest/family mode.

import axios from "axios";
import { API } from "../constants";

const KEY = "poodletalk_hotel_device_token";
const CACHE_KEY = "poodletalk_hotel_snapshot";

export function getHotelToken() {
  try { return localStorage.getItem(KEY) || ""; } catch { return ""; }
}

export function setHotelToken(token) {
  try {
    if (token) localStorage.setItem(KEY, token);
    else localStorage.removeItem(KEY);
  } catch {}
}

export function getCachedHotel() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function setCachedHotel(snapshot) {
  try {
    if (snapshot) localStorage.setItem(CACHE_KEY, JSON.stringify(snapshot));
    else localStorage.removeItem(CACHE_KEY);
  } catch {}
}

export function clearHotelSession() {
  setHotelToken("");
  setCachedHotel(null);
  try { window.dispatchEvent(new CustomEvent("pt_hotel_changed")); } catch {}
}

/** Server-verified validation — call on app open + before typed chat send. */
export async function validateHotel() {
  const token = getHotelToken();
  if (!token) return null;
  try {
    const r = await axios.post(`${API}/hotel/validate`, { device_token: token });
    setCachedHotel(r.data.hotel);
    return r.data.hotel;
  } catch (e) {
    if (e?.response?.status === 401 || e?.response?.status === 403) {
      clearHotelSession();
    }
    return null;
  }
}

export async function activateHotelCode(code, deviceHint, sector) {
  const body = {
    code: (code || "").trim().toUpperCase(),
    device_hint: deviceHint || "",
  };
  if (sector) body.sector = sector;
  const r = await axios.post(`${API}/hotel/activate`, body);
  setHotelToken(r.data.device_token);
  setCachedHotel(r.data.hotel);
  window.dispatchEvent(new CustomEvent("pt_hotel_changed"));
  return r.data.hotel;
}

/** Returns { memberships, addons } for the credits page. */
export async function fetchHotelPlans(sector) {
  const params = sector ? `?sector=${encodeURIComponent(sector)}` : "";
  const r = await axios.get(`${API}/hotel/plans${params}`);
  return r.data;
}

export async function submitHotelForm(form) {
  const r = await axios.post(`${API}/hotel/pending-order`, form);
  return r.data;
}

export async function startMembershipCheckout(pending_id) {
  const r = await axios.post(`${API}/hotel/checkout`, {
    pending_id,
    origin_url: window.location.origin,
  });
  return r.data;
}

export async function startAddonCheckout(price_id, extra = {}) {
  const body = {
    price_id,
    origin_url: window.location.origin,
  };
  const tok = getHotelToken();
  if (tok) body.device_token = tok;
  if (extra.manager_email) body.manager_email = extra.manager_email.trim();
  if (extra.code) body.code = extra.code.trim().toUpperCase();
  const r = await axios.post(`${API}/hotel/addon-checkout`, body);
  return r.data;
}

/** Header for /api/chat when in Hotel Mode. Returns {} for family users. */
export function hotelAuthHeaders() {
  const t = getHotelToken();
  return t ? { "X-Hotel-Token": t } : {};
}

/** Convenience — true when we have a device token AND cached hotel is active. */
export function isHotelModeActive() {
  const t = getHotelToken();
  if (!t) return false;
  const s = getCachedHotel();
  return !!(s && s.status === "active");
}
